import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { Id as _Id } from './id'
import { Identifiable as _Identifiable } from './identifiable'
import { Model } from './model'
import { Term } from './term'
import { Type } from './type'

export interface Purpose<TId extends Purpose.Id = Purpose.Id>
  extends Model<TId> {}
export namespace Purpose {
  export const isPurpose: TypeChecker<Purpose> = TypeChecker.Factory.lazy(() =>
    Model.getTypeChecker(Id.isId),
  ).usingAlias('Purpose')
  /**
   * Just checks if the object's ID indicates that it is a purpose. This can be
   * used to distinguish between various model types without checking that every
   * field of the model is correct.
   */
  export const shouldBePurpose = TypeChecker.Factory.lazy(() =>
    Model.getLooseTypeChecker(Type.Purpose),
  )

  export interface Input extends Omit<Purpose, 'id' | 'terms'> {
    partialId?: PartialId.Standard
    termIds?: readonly Term.Id[]
  }
  export namespace Input {
    export const isInput: TypeChecker<Input> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        TypeChecker.Factory.omit(isPurpose, ['id']),
        TypeChecker.Factory.object<Difference<Input, Purpose>>({
          partialId: TypeChecker.Factory.optional(
            PartialId.Standard.isStandard,
          ),
          termIds: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Term.Id.isId),
          ),
        }),
      ),
    ).usingAlias('Purpose.Input')
  }

  export type Updates = Partial<Omit<Input, 'partialId'>>
  export namespace Updates {
    export const isUpdates: TypeChecker<Updates> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          TypeChecker.Factory.omit(isPurpose, ['id']),
          TypeChecker.Factory.object<Difference<Updates, Input>>({
            name: TypeChecker.Factory.optional(TypeChecker.isString),
          }),
        ),
    ).usingAlias('Purpose.Updates')
  }

  export type Identifiable<TPartialId extends PartialId = PartialId> =
    _Identifiable.ForSpecificType<Type.Purpose, TPartialId, Id.Context>
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.lazy(() =>
        _Identifiable.ForSpecificType.getTypeChecker(
          Type.Purpose,
          Id.Context.isContext,
        ),
      ).usingAlias('dms.models.Purpose.Identifiable')
  }

  export type Id<TPartialId extends PartialId = PartialId> = _Id<
    Type.Purpose,
    TPartialId,
    Id.Context
  >
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      _Id.getTypeChecker(Type.Purpose, Context.isContext),
    ).usingAlias('Purpose.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<infer TPartialId>
        ? Id<TPartialId>
        : never

    export function from<TIdentifiable extends Identifiable>(
      source: TIdentifiable,
    ): Of<TIdentifiable>
    export function from(source: Identifiable): Id {
      return _Id.from<Type.Purpose, PartialId, Context>(
        _Identifiable.ForSpecificType.bind(source, Type.Purpose),
      )
    }

    export type Context = _Id.Context.None
    export namespace Context {
      export const isContext: TypeChecker<Context> =
        _Id.Context.None.isNone.usingAlias('Purpose.Id.Context')
    }
  }
}
